import { getAllocation, searchHotels } from '@/lib/allocation/api';
import { agreedNights, stayWindow, type StayPlanView } from '@/lib/allocation/stay';
import { getReservations } from '@/lib/booking/api';
import { sumPrices } from '@/lib/allocation/price';
import type { HotelBoardBooking, IHotelBoard, IHotelOccupancy } from './types';

// Scope-neutral core of the "who's where" hotel-occupancy board. A scope adapter
// (event: walk the disruption roster; case: read the case's groups) collects the
// HOTEL bookings per hotel + the pending count, then hands them here. This fold
// groups, sorts, and enriches each hotel with geo (stars / distance / rate / real
// name, via the booking hotel-search) and real spend/rooms (via the case
// reservations) — none of which is scope-specific. Built ENTIRELY from existing
// endpoints; no new backend.

/**
 * Friendly hotel label from a `urn:hotel:<slug>:vendor:<v>` URN. The slug is
 * `<airport?>-<name-words>` (e.g. `mad-holiday-inn-express`): a leading 3-letter
 * token is the airport code, the rest title-cased into the name.
 */
export function hotelLabelFromUrn(hotelUrn: string): { name: string; airport?: string } {
  const slug = hotelUrn.split(':')[2] ?? hotelUrn;
  const parts = slug.split('-').filter(Boolean);
  if (parts.length === 0) return { name: hotelUrn };
  const head = parts[0]!;
  const isAirport = /^[a-z]{3}$/.test(head) && parts.length > 1;
  const nameWords = isAirport ? parts.slice(1) : parts;
  const name = nameWords.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  return isAirport ? { name, airport: head.toUpperCase() } : { name };
}

export interface FoldHotelBoardInput {
  /** HOTEL bookings grouped by `hotelUrn` — collected by the scope adapter. */
  groupsByHotel: Map<string, HotelBoardBooking[]>;
  /** Case URNs touched (for the per-case reservation spend/rooms join). */
  caseUrns: Set<string>;
  /** HOTEL-kind bookings still awaiting a reservation. */
  pendingPnrs: number;
  /** The scope source hit its page cap (board may be partial). */
  truncated: boolean;
  /** URN passed to `searchHotels` to satisfy its request shape (it keys on airport,
   *  not this value) — the disruption URN at event scope, the case URN at case scope. */
  scopeUrn: string;
}

/** Fold collected per-hotel bookings into the enriched, sorted occupancy board. */
export async function foldHotelBoard(input: FoldHotelBoardInput): Promise<IHotelBoard> {
  const { groupsByHotel, caseUrns, pendingPnrs, truncated, scopeUrn } = input;

  const hotels: IHotelOccupancy[] = [...groupsByHotel.entries()].map(([hotelUrn, pnrs]) => {
    const { name, airport } = hotelLabelFromUrn(hotelUrn);
    return {
      hotelUrn,
      name,
      ...(airport ? { airport } : {}),
      pnrs: [...pnrs].sort((a, b) => a.pnrLabel.localeCompare(b.pnrLabel)),
      paxCount: pnrs.reduce((sum, g) => sum + (g.paxCount ?? 0), 0)
    };
  });

  // BUG-660: Fetch allocation data FIRST to get stay plans for both:
  // 1. Deriving maxGuests and longest stay window for hotel searches
  // 2. Enriching each group with its night count (as before)
  const planByGroup = new Map<string, StayPlanView>();
  if (caseUrns.size > 0) {
    const stayLists = await Promise.all(
      [...caseUrns].map(async (caseUrn) => {
        try {
          return await getAllocation(caseUrn);
        } catch {
          return null;
        }
      })
    );
    for (const view of stayLists) {
      for (const g of view?.stay?.groups ?? []) {
        if (g.stayPlan) planByGroup.set(g.subCaseUrn, g.stayPlan);
      }
    }
  }

  // BUG-660: Compute maxGuests from all groups in the roster.
  // Per docs: vendors price/check capacity per-booking, not per-case.
  // Use maxGuests (largest group) to ensure hotels returned can accommodate ANY group.
  const allGroups = [...groupsByHotel.values()].flat();
  const maxGuests = allGroups.length > 0
    ? Math.max(1, ...allGroups.map((g) => g.paxCount ?? 0))
    : 1;

  // BUG-660: Compute longest stay window from allocation data.
  // The enrichment search needs a window that covers the longest-staying group.
  const stayPlans = [...planByGroup.values()].filter(
    (plan): plan is StayPlanView => !!plan && typeof plan.nights === 'number'
  );
  const longestPlan = stayPlans.length > 0
    ? stayPlans.reduce((best, plan) => (plan.nights > (best.nights ?? 0) ? plan : best))
    : null;
  const stay = stayWindow(longestPlan);

  // Enrich with geo: one hotel-search per distinct airport, joined on hotelUrn.
  // BUG-660: Use maxGuests and stay window instead of hardcoded guests:1 and default now→now+24h
  const airports = [...new Set(hotels.map((h) => h.airport).filter((a): a is string => !!a))];
  if (airports.length > 0) {
    const offerLists = await Promise.all(
      airports.map((airportCode) =>
        searchHotels({ caseUrn: scopeUrn, airportCode, guests: maxGuests, stay }).catch(() => [])
      )
    );
    const offerByUrn = new Map(offerLists.flat().map((o) => [o.hotelUrn, o]));
    for (const h of hotels) {
      const o = offerByUrn.get(h.hotelUrn);
      if (!o) continue;
      if (o.name) h.name = o.name;
      if (o.stars) h.stars = o.stars;
      if (o.distanceKm) h.distanceKm = o.distanceKm;
      // The airline's price per night (BFF-priced); `null` = not available (no price
      // card) and the card says so. A 0 rate is a stub offer — nothing to show.
      if (o.ratePerRoomUSD !== 0) h.perNightUsd = o.ratePerRoomUSD;
      if (o.geo) h.geo = o.geo;
      if (o.amenities?.length) h.amenities = o.amenities;
    }
  }

  // Enrich each group with its stay plan (the allocation data was already fetched above).
  if (planByGroup.size > 0) {
    for (const h of hotels) {
      h.pnrs = h.pnrs.map((g) => {
        const plan = planByGroup.get(g.subCaseUrn);
        return plan ? { ...g, stay: plan } : g;
      });
      // One number for the hotel only when its guests agree on one — a mixed
      // hotel reads as mixed, never as an average nobody is staying.
      const nights = agreedNights(h.pnrs.map((g) => ({ stayPlan: g.stay ?? null })));
      if (h.pnrs.some((g) => g.stay)) h.nights = nights;
    }
  }

  // Real spend + rooms: sum the LIVE case reservations per hotel (one fetch per case) —
  // held (PENDING = reversible offer awaiting the passenger) AND booked (confirmed).
  // A PNR is not one room, and a passenger pair does not establish a reservation.
  // Keep counts unknown when booking is unavailable; never invent room units.
  if (caseUrns.size > 0) {
    let reservationsUnavailable = false;
    const resvLists = await Promise.all(
      [...caseUrns].map((caseUrn) => getReservations(caseUrn).catch(() => {
        reservationsUnavailable = true;
        return [];
      }))
    );
    const live = resvLists.flat().filter((r) =>
      (r.status === 'BOOKED' || r.status === 'PENDING' || r.status === 'HELD') && r.hotelUrn);
    const validRooms = (rooms: number | undefined): rooms is number =>
      rooms !== undefined && Number.isSafeInteger(rooms) && rooms > 0;
    for (const h of hotels) {
      const reservations = live.filter((r) => r.hotelUrn === h.hotelUrn);
      h.pnrs = h.pnrs.map((g) => {
        const matches = reservations.filter((r) => g.hotelReservationUrn
          ? r.reservationUrn === g.hotelReservationUrn && (!r.subCaseUrn || r.subCaseUrn === g.subCaseUrn)
          : r.subCaseUrn === g.subCaseUrn);
        const reservation = matches.length === 1 ? matches[0] : undefined;
        return reservation && validRooms(reservation.rooms) ? { ...g, rooms: reservation.rooms } : g;
      });
      if (reservations.length > 0 && !reservationsUnavailable) {
        // The airline's PRICE (each row priced by the BFF). One unpriced row (no price
        // card) makes the sum unknown — `null`, «No disponible» — never a partial sum.
        h.spendUsd = sumPrices(reservations.map((r) => r.total));
        h.spendHeldUsd = sumPrices(reservations.filter((r) => r.status !== 'BOOKED').map((r) => r.total));
        // A partial sum would look like the hotel's complete room count.
        if (reservations.every((r) => validRooms(r.rooms))) {
          h.roomsBooked = reservations.reduce((sum, r) => sum + r.rooms!, 0);
          h.roomsHeld = reservations.filter((r) => r.status !== 'BOOKED').reduce((sum, r) => sum + r.rooms!, 0);
        }
        h.estimated = false;
      }
    }
  }

  hotels.sort((a, b) => b.paxCount - a.paxCount || a.name.localeCompare(b.name));

  return {
    hotels,
    totalPnrs: hotels.reduce((sum, h) => sum + h.pnrs.length, 0),
    totalPax: hotels.reduce((sum, h) => sum + h.paxCount, 0),
    airports,
    pendingPnrs,
    truncated
  };
}
